/**
 * Detects the highest Emoji spec version the current platform's font can render.
 *
 * We render native unicode (no sprite sheet), so coverage depends entirely on the
 * OS font — and older systems (e.g. a 2024 macOS) simply lack the newest glyphs,
 * which then show as hollow ".notdef" tofu boxes. Rather than probe all ~1,900
 * emoji individually (slow, and tofu detection is unreliable per-glyph), we probe
 * one representative emoji per spec version *once* and filter the dataset by the
 * detected ceiling.
 *
 * Detection trick: native emoji fonts are *color* fonts, so a supported glyph
 * paints saturated color pixels regardless of our `fillStyle`. A missing glyph
 * falls back to a monochrome tofu box / text, which stays black. So "did this
 * probe paint any colored pixel" cleanly separates supported from unsupported,
 * and (unlike the old center-alpha heuristic) doesn't get fooled by tofu boxes.
 */

const SIZE = 24;

/**
 * Single-codepoint emoji introduced in each Emoji spec version, newest last.
 * Single codepoints render as one glyph (or one tofu box), so detection is
 * unambiguous — and these are all warm-colored faces, so the color-pixel probe
 * has a strong signal. ZWJ sequences are deliberately avoided (their fallback
 * width/colour is platform-dependent and noisy).
 */
const VERSION_PROBES: ReadonlyArray<{ version: number; emoji: string }> = [
  { version: 11, emoji: '🥰' }, // U+1F970 smiling face with hearts
  { version: 12, emoji: '🥱' }, // U+1F971 yawning face
  { version: 13, emoji: '🥲' }, // U+1F972 smiling face with tear
  { version: 14, emoji: '🫠' }, // U+1FAE0 melting face
  { version: 15, emoji: '🫨' }, // U+1FAE8 shaking face
  { version: 16, emoji: '🫩' }, // U+1FAE9 face with bags under eyes
];

// `undefined` = not yet probed, `null` = no canvas available (SSR / tests).
let ctx: CanvasRenderingContext2D | null | undefined;

function context(): CanvasRenderingContext2D | null {
  if (ctx !== undefined) return ctx;
  if (typeof document === 'undefined') {
    ctx = null;
    return ctx;
  }
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const c = canvas.getContext('2d', { willReadFrequently: true });
  if (c) {
    c.textBaseline = 'top';
    c.font = `${SIZE}px sans-serif`;
    c.fillStyle = '#000'; // so any colour we see came from a color-emoji glyph
  }
  ctx = c;
  return ctx;
}

/** True if drawing `emoji` paints at least one saturated (coloured) pixel. */
function rendersInColor(c: CanvasRenderingContext2D, emoji: string): boolean {
  c.clearRect(0, 0, SIZE, SIZE);
  c.fillText(emoji, 0, 0);
  const { data } = c.getImageData(0, 0, SIZE, SIZE);
  for (let i = 0; i < data.length; i += 4) {
    if (data[i + 3] < 16) continue; // effectively transparent
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    // A colour glyph has channels that diverge; black tofu/text does not.
    if (Math.max(r, g, b) - Math.min(r, g, b) > 24) return true;
  }
  return false;
}

/**
 * Highest Emoji spec version the platform renders. Versions are assumed
 * monotonic (if it can't draw vN it won't draw vN+1), so we stop at the first
 * gap. Returns `Infinity` when detection is unavailable (SSR/tests) or when even
 * the newest probe renders — in both cases nothing is filtered out.
 */
export function detectMaxEmojiVersion(): number {
  const c = context();
  if (!c) return Infinity;

  let supported = VERSION_PROBES[0].version - 1; // nothing in probe range yet
  for (const probe of VERSION_PROBES) {
    if (!rendersInColor(c, probe.emoji)) break;
    supported = probe.version;
  }
  return supported === VERSION_PROBES[VERSION_PROBES.length - 1].version
    ? Infinity
    : supported;
}
